import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, SEOHead } from '../components';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  PresentationChartLineIcon,
  BeakerIcon,
  ArrowRightIcon,
  CheckIcon,
  StarIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

const GoogleAdsPage: React.FC = () => {
  const navigate = useNavigate();

  // SEO setup
  useSEO(pageConfigs.googleAds);

  // Navigation handlers
  const handleGetAudit = () => {
    navigate('/consultation');
  };

  const handleViewCaseStudies = () => {
    navigate('/case-studies');
  };

  const handleScheduleConsultation = () => {
    navigate('/consultation');
  };

  const handleGetStarted = () => {
    navigate('/quote');
  };

  const handleLearnMore = () => {
    navigate('/contact');
  };
  const features = [
    {
      title: "Google Ads Management",
      description: "Strategic management of your Google Ads campaigns to maximize visibility and conversions.",
      icon: <MagnifyingGlassIcon className="w-8 h-8" />,
      benefits: ["Search campaigns", "Display campaigns", "Shopping campaigns", "Video campaigns"]
    },
    {
      title: "Google My Business Optimization",
      description: "Complete optimization of your Google Business Profile to maximize local visibility and engagement.",
      icon: <BuildingOfficeIcon className="w-8 h-8" />,
      benefits: ["Profile optimization", "Review management", "Post scheduling", "Insights tracking"]
    },
    {
      title: "Google Analytics Implementation",
      description: "Comprehensive analytics setup and management to track performance and make data-driven decisions.",
      icon: <PresentationChartLineIcon className="w-8 h-8" />,
      benefits: ["GA4 setup", "Conversion tracking", "Custom reporting", "User behavior analysis"]
    },
    {
      title: "Conversion Rate Optimization",
      description: "Strategic optimization of your landing pages and website to maximize conversion rates.",
      icon: <BeakerIcon className="w-8 h-8" />,
      benefits: ["A/B testing", "Landing page optimization", "User experience improvements", "Conversion tracking"]
    }
  ];

  const process = [
    {
      step: "01",
      title: "Account Analysis",
      description: "We analyze your current Google ecosystem performance, account structure, and historical data."
    },
    {
      step: "02",
      title: "Strategy Development",
      description: "Create a comprehensive Google management strategy tailored to your business goals."
    },
    {
      step: "03",
      title: "Implementation",
      description: "Set up campaigns, optimize your Google Business Profile, and implement proper tracking."
    },
    {
      step: "04",
      title: "Optimization & Growth",
      description: "Continuously monitor, test, and optimize your Google presence to maximize results."
    }
  ];

  const caseStudy = {
    client: "Local Service Business",
    industry: "Home Services",
    challenge: "Low visibility in local search and inefficient Google Ads spending",
    solution: "Implemented comprehensive Google ecosystem management including Ads, My Business, and Analytics",
    results: [
      "#1 ranking for 90% of target keywords",
      "65% reduction in cost per lead",
      "320% increase in qualified leads",
      "4.8 star average rating with 150+ reviews"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-100 rounded-full px-4 py-2 mb-6">
                <MagnifyingGlassIcon className="w-5 h-5 text-blue-600" />
                <span className="text-blue-700 font-semibold text-sm">Google Certified Experts</span>
              </div>
              
              <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
                Complete 
                <span className="text-blue-600"> Google Account Management</span>
              </h1>
              
              <p className="text-xl text-slate-700 mb-8 leading-relaxed">
                Maximize your visibility and performance across the entire Google ecosystem.
                Our comprehensive approach ensures optimal results.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button 
                  onClick={handleGetAudit}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Get Google Account Audit</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleViewCaseStudies}
                  className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg border-2 border-slate-200 transition-colors"
                >
                  View Case Studies
                </button>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">#1</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Search Ranking</div>
                  <div className="text-xs text-slate-500">For target keywords</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">65%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">CPL Reduction</div>
                  <div className="text-xs text-slate-500">Cost per lead</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">320%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Lead Increase</div>
                  <div className="text-xs text-slate-500">Qualified leads</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">4.8★</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Rating</div>
                  <div className="text-xs text-slate-500">Average review</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                <div className="flex items-center space-x-3 mb-6">
                  <MagnifyingGlassIcon className="w-6 h-6 text-blue-600" />
                  <h3 className="text-2xl font-display font-bold text-slate-900">Free Google Account Audit</h3>
                </div>
                <p className="text-slate-600 mb-6">
                  Get a comprehensive analysis of your current Google ecosystem performance and 
                  receive a customized strategy to improve your results.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Google Ads account review</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Google My Business analysis</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Custom optimization recommendations</span>
                  </div>
                </div>
                
                <button 
                  onClick={handleGetAudit}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Get Your Free Audit
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Comprehensive Google Ecosystem Management
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our complete approach ensures maximum visibility and performance across all Google platforms
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-slate-100">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <div className="text-blue-600">{feature.icon}</div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckIcon className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Our 4-Step Process
            </h2>
            <p className="text-xl text-slate-600">
              Proven methodology for Google ecosystem success
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">{step.step}</span>
                </div>
                <h3 className="text-xl font-display font-bold text-slate-900 mb-4">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center space-x-2 bg-green-100 rounded-full px-4 py-2 mb-6">
                  <StarIcon className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 font-semibold text-sm">Success Story</span>
                </div>
                
                <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6">
                  {caseStudy.client}
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Challenge</h4>
                    <p className="text-slate-600">{caseStudy.challenge}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Solution</h4>
                    <p className="text-slate-600">{caseStudy.solution}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Results</h4>
                    <div className="space-y-2">
                      {caseStudy.results.map((result, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckIcon className="w-5 h-5 text-green-600" />
                          <span className="text-slate-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">🔍</div>
                  <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">Ready to dominate Google search?</h3>
                  <p className="text-slate-600">Schedule a consultation with our Google experts</p>
                </div>
                
                <button 
                  onClick={handleScheduleConsultation}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors mb-4"
                >
                  Schedule Consultation
                </button>
                
                <button 
                  onClick={handleViewCaseStudies}
                  className="w-full bg-white hover:bg-gray-50 text-slate-900 font-semibold py-3 px-6 rounded-lg border-2 border-slate-200 transition-colors"
                >
                  View More Case Studies
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Services Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Google Services We Manage
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive management across the entire Google ecosystem
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">🔍</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Google Ads</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">🏢</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Google My Business</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">📊</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Google Analytics</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">🔧</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Search Console</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">🛒</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Google Shopping</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">📱</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Google Tag Manager</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">📹</div>
              <h3 className="text-xl font-display font-bold text-slate-900">YouTube Ads</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">📝</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Google Merchant Center</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Certification Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Google Certified Experts
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our team holds all major Google certifications
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">Google Ads Certified</h3>
              <p className="text-slate-600">Search, Display, Video, Shopping</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">Google Analytics Certified</h3>
              <p className="text-slate-600">GA4 Implementation & Analysis</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">Google Premier Partner</h3>
              <p className="text-slate-600">Top 3% of Google Partners</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-display font-bold mb-6">
              Ready to Dominate Google Search?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Get started with a free Google account audit and custom strategy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleGetStarted}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors"
              >
                Get Started
              </button>
              <button 
                onClick={handleLearnMore}
                className="bg-transparent hover:bg-white/10 text-white font-semibold py-4 px-8 rounded-lg border-2 border-white/20 transition-colors"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GoogleAdsPage;